import { useState, useEffect } from 'react'
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'
import { InputContainer, GoogleAutocomplete, Markdown } from 'components/ideal-core-ui'
import s from './AddEventModal.scss'
import Select from 'react-select'
import cn from 'classnames'
import DatePicker from 'react-datepicker'
import '!style-loader!css-loader!react-datepicker/dist/react-datepicker.css' // eslint-disable-line
import moment from 'moment'
import { getUser, getDateFormat, getTimezone } from 'utils/authService'
import { toCompaniesSelect } from 'components/Companies/serializers'
import { PaginatedSelect } from 'components/common/PaginatedSelect'
import { makeGetRequest } from 'utils/api'
import { toContacts } from './serializers'

const notificationTypes = [
  {
    value: 'MINUTE',
    label: 'Minutes'
  },
  {
    value: 'HOUR',
    label: 'Hours'
  },
  {
    value: 'DAY',
    label: 'Days'
  },
  {
    value: 'WEEK',
    label: 'Weeks'
  }
]

const AddEventModal = ({
  deleteEvent,
  updateEvent,
  event,
  toggleAddEventModal,
  showAddEventModal,
  saveEvent,
  users,
  setEvent
}) => {
  const [company, setCompany] = useState(null)
  const [contacts, setContacts] = useState([])
  const syncEndDate = start => {
    const { end } = event
    if (moment(start).isAfter(moment(end))) {
      const end = moment(start).add(1, 'hours').toDate()
      setEvent(draft => {
        draft.end = end
      })
    }
  }

  useEffect(() => {
    ;(async () => {
      if (!company) return
      const contacts = await makeGetRequest(`/v1/contacts/company/${company.value}`)
      setContacts(toContacts(contacts))
    })()
  }, [company])

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={showAddEventModal}
        toggle={toggleAddEventModal}
        size="lg"
      >
        <ModalHeader toggle={toggleAddEventModal} className={s.modalHeader}>
          {event.id ? 'Edit Calendar Event' : 'Add Calendar Event'}
        </ModalHeader>
        <ModalBody>
          <label className={s.label}>*Event Title</label>
          <InputContainer
            type="text"
            text={event.title}
            placeholder="Enter Event Title"
            cb={value =>
              setEvent(draft => {
                draft.title = value
              })
            }
          />
          <div className={cn(s.dateWrapper, s.marginTop30)}>
            <div className={cn(s.date, s.pullLeft)}>
              <label className={s.label}>Start Date/Time</label>
              <DatePicker
                selected={event.start}
                selectsStart
                startDate={event.start}
                endDate={event.end}
                onChange={value => {
                  if (!value) return
                  setEvent(draft => {
                    draft.start = value
                  })
                  syncEndDate(value)
                }}
                showTimeSelect
                timeFormat="h:mm aa"
                timeIntervals={15}
                dateFormat={`${getDateFormat()
                  .replace('YYYY', 'yyyy')
                  .replace('DD', 'dd')} h:mm aa`}
                timeCaption="time"
                placeholderText="Select start date"
                className={s.calendar}
              />
            </div>
            <div className={cn(s.date, s.pullLeft, s.marginRight15)}>
              <label className={s.label}>End Date/Time</label>
              <DatePicker
                selected={
                  event.end || (event.start && moment(event.start).add(1, 'hours').toDate())
                }
                selectsEnd
                startDate={event.start}
                endDate={event.end}
                onChange={value => {
                  setEvent(draft => {
                    draft.end = value
                  })
                }}
                minDate={event.start}
                showTimeSelect
                timeFormat="h:mm aa"
                timeIntervals={15}
                dateFormat={`${getDateFormat()
                  .replace('YYYY', 'yyyy')
                  .replace('DD', 'dd')} h:mm aa`}
                timeCaption="time"
                placeholderText="Select end date"
                className={s.calendar}
              />
            </div>
            <div className={cn(s.timezone, s.pullLeft)}>
              <p>Timezone:</p>
              <span>{getTimezone()}</span>
            </div>
          </div>
          <div className={cn(s.marginTop30, s.pullLeft)}>
            <label className={s.label}>Event Location</label>
            <GoogleAutocomplete
              value={event.location || ''}
              onChange={value =>
                setEvent(draft => {
                  draft.location = value
                })
              }
              onSelectCallback={params =>
                setEvent(draft => {
                  draft.location = params.address
                })
              }
              className={s.location}
            />
          </div>
          <div className={cn(s.notifications, s.marginTop30, s.pullLeft)}>
            <label className={s.label}>Email Notifications</label>
            <Select
              value={event.reminders}
              onChange={value =>
                setEvent(draft => {
                  draft.reminders = value
                })
              }
              options={[
                {
                  value: true,
                  label: 'Reminder'
                },
                { value: false, label: 'No Reminder' }
              ]}
              className={s.reminders}
            />
            <InputContainer
              type="number"
              style={{ width: '80px', float: 'left' }}
              text={event.notifications.time}
              cb={value =>
                setEvent(draft => {
                  draft.notifications.time = value
                })
              }
            />
            <Select
              value={
                event.notifications.type.value
                  ? event.notifications.type
                  : notificationTypes.find(n => n.value === event.notifications.type)
              }
              onChange={value =>
                setEvent(draft => {
                  draft.notifications.type = value
                })
              }
              options={notificationTypes}
              className={s.notificationsType}
            />
          </div>
          <div className={s.clearfix} />
          <hr />
          <label className={s.label}>*Attendees</label>
          {![...event.users, ...event.contacts].length ? (
            <p>
              <i>Please add attendees</i>
            </p>
          ) : (
            <ul className={s.itemsWrapper}>
              {[...event.users, ...event.contacts].map(item => (
                <li key={item.value}>
                  <span className={s.item}>{item.label}</span>
                  <span
                    className={s.removeItem}
                    onClick={() =>
                      setEvent(draft => {
                        const index = draft[item.type].findIndex(i => i.value === item.value)
                        draft[item.type].splice(index, 1)
                      })
                    }
                  >
                    &times;
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div style={{ overflow: 'hidden' }}>
            <div className={s.pullLeft}>
              <label className={s.label}>Add Internal Attendees</label>
              <Select
                value={null}
                onChange={value =>
                  setEvent(draft => {
                    draft.users.push({ ...value, type: 'users' })
                  })
                }
                options={users}
                className={s.users}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                menuPortalTarget={document.body}
              />
            </div>
            <div className={cn(s.pullLeft, s.company)}>
              <label className={s.label}>Select Company</label>
              <PaginatedSelect
                url="/v1/companies"
                value={company}
                className={s.users}
                onChange={value => setCompany(value)}
                serializer={toCompaniesSelect}
                isPortal
              />
            </div>
            {company && (
              <div className={s.pullLeft}>
                <label className={s.label}>Select Contact</label>
                <Select
                  value={null}
                  onChange={value =>
                    setEvent(draft => {
                      draft.contacts.push({ ...value, type: 'contacts' })
                    })
                  }
                  options={contacts}
                  className={s.users}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  menuPortalTarget={document.body}
                />
              </div>
            )}
          </div>
          <label className={cn(s.label, s.marginTop30)}>Description</label>
          <Markdown
            initialValue={event.description}
            saveCb={value =>
              setEvent(draft => {
                draft.description = value
              })
            }
          />
        </ModalBody>
        <ModalFooter>
          {event && getUser().id === event.createdBy?.id && (
            <Button className={s.delete} color="danger" onClick={deleteEvent}>
              Delete
            </Button>
          )}
          <Button
            className={s.saveBtn}
            disabled={!event.title || (!event.users.length && !event.contacts.length)}
            onClick={() => {
              event.id
                ? updateEvent({ sendInviteEmail: true })
                : saveEvent({ sendInviteEmail: true })
            }}
          >
            Save & Send Invites
          </Button>
          <Button
            className={s.saveBtn}
            disabled={!event.title || (!event.users.length && !event.contacts.length)}
            onClick={() => {
              event.id
                ? updateEvent({ sendInviteEmail: false })
                : saveEvent({ sendInviteEmail: false })
            }}
          >
            Save
          </Button>
          <Button color="secondary" onClick={toggleAddEventModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default AddEventModal
